import React, { useMemo, useState } from "react";
import { ColumnDef, SortingState } from "@tanstack/react-table";
import { InvoiceDocument, VisibleColumnSpec } from "@/types";
import { useTranslation } from "react-i18next";
import NonSortableHeader from "@/components/molecules/NonSortableHeader";
import { Group } from "@mantine/core";
import styles from "./styles.module.css";
import SVGAsset from "@/components/atoms/SVGAsset/SVGAsset";
import dayjs from "dayjs";
import { useAuth } from "@/contexts/AuthContext";
import DataTable from "@/components/molecules/DataTable";
import CTA from "@/components/atoms/CTA";
import { useVisibleColumns } from "@/hooks/useVisibleColumns";
import { useGetInvoicesForCompanyId } from "@/services/Accounting";

interface TasksTableProps {
  /** Define a column spec of what rows and columns to display and when **/
  visibleColumns?: VisibleColumnSpec<InvoiceDocument, "actions">[];
  /** Define a predefined sort order **/
  initialSort?: SortingState;
  /** Whether to show client-side pagination */
  pagination?: boolean;
  /** Decide whether to enable or disable actions **/
  actionable?: boolean;
  /** Optional rows per page for client-side pagination */
  initialRowsPerPage?: number;
}

/**
 * A reusable task table component, with sorting, filtering, actions and pagination.
 * @param TaskTableProps - Component props
 * @param visibleColumns - Constant Array defined to manage which columns to display and when
 * @param initialSort - Define a predefined sort order
 * @param pagination - Boolean to enable or disable pagination
 * @param initialRowsPerPage - set maximum number of rows to display used for snapshots
 *
 **/
const InvoiceTable: React.FC<TasksTableProps> = ({
  visibleColumns,
  initialSort = null,
  pagination = true,
  actionable: _actionable = true,
  initialRowsPerPage = 15,
}) => {
  const [, setSelectedRow] = useState<InvoiceDocument | undefined>(undefined);

  const { currentCompany } = useAuth();

  const { data: invoices, isLoading } = useGetInvoicesForCompanyId(
    currentCompany?.companyId as number,
    5,
    false,
    false,
    "DESC",
    {
      enabled: !!currentCompany?.companyId,
    }
  );

  const { t } = useTranslation();
  const currentDate = dayjs().format();

  const baseColumns: ColumnDef<InvoiceDocument>[] = useMemo(
    () => [
      {
        header: () => <span />,
        id: "icon",
        size: 50,
        minSize: 50,
        maxSize: 50,
        cell: ({ row }) =>
          row.original.invoicePaid ? <SVGAsset name="arrowPurchaseGreen" /> : <SVGAsset name="arrowPurchaseRed" />,
      },
      {
        header: ({ column }) => <NonSortableHeader column={column} label={t("common:date")} />,
        id: "created",
        cell: ({ row }) => {
          return dayjs(row.original.created).format("Do MMMM YYYY, HH:mm");
        },
      },
      {
        header: ({ column }) => <NonSortableHeader column={column} label={t("tasks:counterParty")} />,
        id: "buyerCompanyName",
        cell: ({ row }) => row.original.buyerCompanyName,
      },
      {
        header: ({ column }) => <NonSortableHeader column={column} label={t("common:sum")} />,
        id: "amount",
        cell: ({ row }) => `${row.original.amount.toFixed(2)} ${row.original.currencyType}`,
      },
      {
        header: ({ column }) => <NonSortableHeader column={column} label={t("dashboard:payed")} />,
        id: "amount",
        cell: ({ row }) => (row.original.invoicePaid ? <SVGAsset name="checkmark-green-noBg" /> : "-"),
      },
      {
        // NEVER SHOWS ON PURPOSE
        id: "actions",
        header: () => "",
        meta: {
          breakpoint: "mantine-hidden-from-xs",
        },
        cell: ({ row }) => {
          return (
            <CTA type={"button"} intent={"secondary"} onClick={() => setSelectedRow(row.original)}>
              {t("actions:showMore")}
            </CTA>
          );
        },
      },
    ],
    [t, currentDate]
  );

  const columns = useVisibleColumns<InvoiceDocument, "actions">(baseColumns, visibleColumns);

  return (
    <Group dir={"col"} className={styles.tableContainer}>
      <DataTable
        onRowClick={undefined}
        data={invoices || []}
        columns={columns}
        isLoading={isLoading}
        mobileHeaderCell={1}
        showPagination={pagination}
        initialSort={initialSort ?? undefined}
        initialRowsPerPage={initialRowsPerPage}
      />
    </Group>
  );
};

export default InvoiceTable;
